import React, { useEffect, useRef, useState } from "react"
import { Alert, Spinner } from "reactstrap"
import { toPublishPath } from "./plannerQuery"
import { UNCONFIRMED } from "./proposalMessages"
import { PASSED_PHASES, categoryLabel, competitionsLabel, dayLabel, daysLabel, placeLabel } from "./plannerLabels"
import { completeness, fixedInEditor, leadTimeLabel, leadTimeTitle, problemLabel, reviewMessage, startsIn } from "./publishingLabels"
import useCalendarRead from "./useCalendarRead"
import useProposalRead from "./useProposalRead"
import useProposalOperation from "./useProposalOperation"
import PublishReview, { ReviewLoading } from "./PublishReview"
import PublishResult from "./PublishResult"
import PlannerDialog from "./PlannerDialog"
import QueueBar from "./QueueBar"

const COLUMNS = ["Start", "Number · Name", "Category", "City · Club", "Organizer", "Competitions", "Lead time"]
// E2p pinned decision Q3: at most 50 proposals are published at a time (the publish command's batch).
const MAX_PUBLISH = 50
const BATCH_REFUSED = "The publish was refused before anything was created. Read Ready to publish again, then review it once more."

// Ready to publish (wayfinder ticket 18; E2p pinned decisions Q1–Q3): the country's confirmed proposals by start date, each with its
// publication preview (Phase E1p) — "Ready", complete, the only ones ticked and published, and "Confirmed, incomplete" with what each
// misses — then the review of the proposals ticked, the publish and its result.
export default function ReadyToPublishTab({ query, read, settings, timeZone, today, commands, onCountry, onEditProposal, onChanged, onShowCalendar, focus,
  onFocusUsed, version }) {
  const mounted = useRef(true)
  useEffect(() => () => { mounted.current = false }, [])
  const proposals = read.data?.proposals || []
  const ready = proposals.filter(proposal => proposal.complete)
  const incomplete = proposals.filter(proposal => !proposal.complete)
  const [picked, setPicked] = useState({})
  const ticked = ready.filter(proposal => picked[proposal.id])
  const full = ticked.length >= MAX_PUBLISH
  const pick = proposal => setPicked(current => {
    const { [proposal.id]: was, ...rest } = current
    return was ? rest : { ...current, [proposal.id]: true }
  })
  const allTicked = ready.length > 0 && (ticked.length === ready.length || ticked.length === MAX_PUBLISH)

  // The review: the proposals of the batch, read again by their ids, and the one shown with its record (for its history and its edits).
  const [review, setReview] = useState(null)
  // A review of these proposals (their numbers kept, to name one that leaves), on `field` of the first one when a problem's link opens it.
  const reviewOf = (list, field = null) => setReview({ ids: list.map(({ id }) => id), current: list[0].id, field,
    numbers: Object.fromEntries(list.map(({ id, number }) => [id, number])) })
  const reviewRead = useCalendarRead(review ? toPublishPath(query.country, review.ids) : null, "The review could not be loaded.")
  const rows = reviewRead.data?.proposals || []
  // The batch as read now: a proposal no longer confirmed (an edit returned it to Proposed) is not in it.
  const batch = review ? review.ids.map(id => rows.find(row => row.id === id)).filter(Boolean) : []
  const gone = review && reviewRead.data ? review.ids.filter(id => !batch.some(row => row.id === id)) : []
  const current = batch.find(row => row.id === review.current) || batch[0] || null
  const record = useProposalRead(current?.id || null)
  // A save in the editor ("Edit proposal") reads the review again (`version`): a proposal no longer confirmed leaves the batch.
  const { reload: reloadReview } = reviewRead
  useEffect(() => { if (version) reloadReview() }, [version, reloadReview])
  // "Publish…" from the quick peek or the editor: that proposal's review.
  useEffect(() => {
    if (!focus) return
    onFocusUsed()
    setReview({ ids: [focus.id], current: focus.id, field: null, numbers: { [focus.id]: focus.number } })
  }, [focus, onFocusUsed])

  // A value filled or changed in the review: an edit of the proposal at the revision read (ticket 18 Q1, E2p-Q2), then everything read
  // again. Only what the organizer did not confirm is changed here, so a confirmed proposal stays confirmed.
  const operation = useProposalOperation(reviewMessage)
  const [notice, setNotice] = useState(null)
  const save = async (changes, what) => {
    setNotice(null)
    const { id, revision } = record.proposal
    const result = await operation.run("edit", "/admin/tournament-proposals/" + id, { revision, changes }, "patch")
    if (!mounted.current || !result || ["cancelled", "busy"].includes(result.status)) return
    setNotice(result.status === "saved" ? { tone: "success", text: `Saved: ${what}. The change is in the proposal's history.` }
      : { tone: "danger", text: result.status === "unknown" ? result.message || UNCONFIRMED : result.message })
    reviewRead.reload()
    record.reload()
    onChanged()
  }
  // A problem's link: its field in the review, or the editor for what the organizer confirmed.
  const fix = (proposal, problem) => (fixedInEditor(problem) ? onEditProposal(proposal)
    : reviewOf([proposal], problem.field))

  // Publishing (ticket 18): the complete proposals of the review, after a confirmation that names them, each published as its own
  // operation; "Email the organizers (n)" is ticked by default. A proposal whose phases passed is published anyway once confirmed. The
  // confirmation keeps only the ids: its words come from the review as read now, as the publish does (CP lesson 2026-09-30).
  const [emailOrganizers, setEmailOrganizers] = useState(true)
  const [confirming, setConfirming] = useState(null)
  const [run, setRun] = useState(null)
  const complete = batch.filter(row => row.complete)
  const organizersOf = list => new Set(list.map(row => row.organizer?.id).filter(Boolean)).size
  const publish = async ids => {
    const sent = rows.filter(row => ids.includes(row.id) && row.complete)
    setConfirming(null)
    // A retry of a proposal no longer complete (or confirmed) goes back to the review, which says why.
    if (!sent.length) {
      setRun(null)
      setNotice({ tone: "danger", text: "Nothing left to publish: it is no longer complete or no longer confirmed. Review it again." })
      return
    }
    setRun({ items: sent, skipped: ids.length > 1 ? batch.filter(row => !row.complete) : [], result: null, error: null })
    const result = await commands.publish({ proposals: sent.map(({ id, revision, passed }) => ({ id, revision, ...(passed.length && { publishAnyway: true }) })),
      emailOrganizers })
    if (!mounted.current || !result || result.status === "cancelled") return
    setRun(previous => ({ ...previous, result: result.status === "saved" ? result : null,
      error: result.status === "saved" ? null : result.status === "unknown" ? result.message || UNCONFIRMED : result.message || BATCH_REFUSED }))
    reviewRead.reload()
  }
  const confirmation = ids => {
    const sent = rows.filter(row => ids.includes(row.id) && row.complete)
    const left = ids.length > 1 ? batch.filter(row => !row.complete) : []
    const late = sent.filter(row => row.passed.length)
    const emailed = organizersOf(sent)
    return { ids, late: late.length > 0, title: sent.length === 1 ? `Publish ${sent[0].number}?` : `Publish ${sent.length} proposals?`, lines: [
      sent.length === 1 ? `Publishing creates the event, its ${sent[0].competitions.length} competitions without fees and its logistics sheet in one operation. Players see the event at once.`
        : "Publishing creates each event with all its competitions, without fees, and its logistics sheet, one operation per event. Players see them at once.",
      ...late.map(row => `${row.number}: ${row.passed.map(phase => PASSED_PHASES[phase]).join("; ")}.`),
      left.length > 0 && `Left out because incomplete: ${left.map(row => row.number).join(", ")}.`,
      emailOrganizers && emailed ? `Their organizers are emailed (${emailed}).` : "No organizer is emailed.",
      "There is no Undo: a published event is disabled from Events Management.",
    ].filter(Boolean) }
  }
  // The confirmation as the review reads now; none when nothing of it is left to publish.
  const shownConfirmation = confirming && rows.some(row => confirming.ids.includes(row.id) && row.complete) ? confirmation(confirming.ids) : null
  const backToList = () => { setRun(null); setReview(null); setPicked({}); setNotice(null) }

  const listRow = (proposal, { box, fix }) => <div role="row" className="ep-list-row ep-publish-row" key={proposal.id}>
    <span role="cell" className="ep-stack" data-label="Start"><b>{dayLabel(proposal.days.start)}</b><span className="ep-meta">{startsIn(proposal.days.start, today)}</span></span>
    <span role="cell" className="ep-stack" data-label="Number · Name">
      <span className="ep-inline">{box}<b>{proposal.number}</b></span>
      <span className="ep-meta">{proposal.name || "No name yet"}</span>
    </span>
    <span role="cell" data-label="Category"><span className={`ep-dot ep-cat-${proposal.category}`} aria-hidden="true" />{categoryLabel(settings, proposal.category)}</span>
    <span role="cell" data-label="City · Club">{placeLabel(proposal)}</span>
    <span role="cell" data-label="Organizer">{proposal.organizer?.name || "No organizer"}</span>
    <span role="cell" data-label="Competitions" className="ep-tiles">{competitionsLabel(proposal.competitions) || "—"}</span>
    <span role="cell" data-label="Lead time">{leadTimeLabel(proposal.closeToStart)
      && <span className="ep-mark ep-mark-close" title={leadTimeTitle(proposal.closeToStart)}>{leadTimeLabel(proposal.closeToStart)}</span>}</span>
    {fix && <span role="cell" data-label="To fix" className="ep-completeness ep-publish-fix">{fix}</span>}
  </div>

  const list = () => <>
    <section className="ep-list" aria-label="Ready">
      <div className="ep-panel-head">
        <h3>{`Ready (${ready.length})`}</h3>
        <span className="ep-spacer" />
        {full && <span className="ep-meta">{`Publish up to ${MAX_PUBLISH} at a time; the next ones after this publish.`}</span>}
        <button type="button" className="btn btn-primary btn-sm" disabled={!ticked.length}
          onClick={() => reviewOf(ticked)}>{`Review and publish (${ticked.length})`}</button>
      </div>
      <div role="table" aria-label="Ready to publish">
        <div role="rowgroup"><div role="row" className="ep-list-row ep-publish-row ep-list-head">
          {COLUMNS.map((column, index) => <span role="columnheader" key={column}>{index === 1
            ? <span className="ep-inline"><input type="checkbox" aria-label="Select all" checked={allTicked} disabled={!ready.length}
              onChange={() => setPicked(allTicked ? {} : Object.fromEntries(ready.slice(0, MAX_PUBLISH).map(({ id }) => [id, true])))} />{column}</span>
            : column}</span>)}
        </div></div>
        <div role="rowgroup">{ready.map(proposal => listRow(proposal, { box: <input type="checkbox" aria-label={`Select ${proposal.number}`}
          checked={Boolean(picked[proposal.id])} disabled={full && !picked[proposal.id]} onChange={() => pick(proposal)} /> }))}</div>
      </div>
      {!ready.length && <div className="ep-empty"><b>Nothing ready to publish</b><span className="ep-meta">Confirmed proposals appear here once complete.</span></div>}
    </section>
    {incomplete.length > 0 && <section className="ep-list" aria-label="Confirmed, incomplete">
      <h3>{`Confirmed, incomplete (${incomplete.length})`}</h3>
      <div role="table" aria-label="Confirmed, incomplete"><div role="rowgroup">
        {incomplete.map(proposal => listRow(proposal, { fix: <>
          <span className="ep-meta">To fix:</span>
          {proposal.problems.map(problem => <button type="button" key={problem.field + problem.reason} className="ep-source is-missing"
            onClick={() => fix(proposal, problem)}>{problemLabel(problem, proposal.competitions)}</button>)}
        </> }))}
      </div></div>
    </section>}
  </>

  const reviewPage = () => {
    if (run) return <PublishResult run={run} commands={commands} onBack={backToList} onShowCalendar={onShowCalendar}
      onFix={item => { setRun(null); reviewOf([item]) }} onRetry={items => publish(items.map(({ id }) => id))} />
    if (reviewRead.error) return <Alert color="danger">{reviewRead.error}{" "}<button type="button" className="btn btn-link p-0 align-baseline" onClick={reviewRead.reload}>Retry</button></Alert>
    if (!reviewRead.data) return <ReviewLoading />
    return <div className="ep-split">
      <nav className="ep-card ep-organizers ep-split-side" aria-label="Proposals to publish">
        {review.ids.map(id => {
          const proposal = rows.find(item => item.id === id)
          if (!proposal) return null
          return <button type="button" key={id} className={"ep-organizer-pick ep-organizer" + (id === current?.id ? " is-on" : "")} aria-pressed={id === current?.id}
            onClick={() => setReview({ ...review, current: id, field: null })}>
            <span className="ep-inline"><b>{proposal.number}</b><span className="ep-spacer" />
              <span className={`ep-mark ep-mark-${completeness(proposal).tone}`}>{completeness(proposal).label}</span></span>
            <span className="ep-meta ep-trunc">{`${daysLabel(proposal.days)} · ${proposal.club?.city || placeLabel(proposal)}`}</span>
          </button>
        })}
      </nav>
      <div className="ep-split-main">
        {gone.map(id => <Alert key={id} color="info">{`${review.numbers[id]} is no longer Confirmed, so it left this publish.`}</Alert>)}
        {notice && <Alert color={notice.tone} role="status">{notice.text}</Alert>}
        {current && <PublishReview key={current.id} row={current} record={record} settings={settings} timeZone={timeZone} saving={Boolean(operation.pending)} onSave={save}
          onEditProposal={onEditProposal} focusField={review.field} />}
        <section className="ep-send-bar" aria-label="Publish">
          <button type="button" className="btn btn-light" onClick={backToList}>Back to Ready to publish</button>
          <span className="ep-spacer" />
          <span className="ep-meta">{`${complete.length} of ${batch.length} complete`}</span>
          <label className="ep-check"><input type="checkbox" checked={emailOrganizers} disabled={!organizersOf(complete)}
            onChange={() => setEmailOrganizers(!emailOrganizers)} />{`Email the organizers (${organizersOf(complete)})`}</label>
          {batch.length > 1 && <button type="button" className="btn btn-light" disabled={!current?.complete}
            onClick={() => setConfirming({ ids: [current.id] })}>Publish this one</button>}
          <button type="button" className="btn btn-primary" disabled={!complete.length}
            onClick={() => setConfirming({ ids: batch.map(({ id }) => id) })}>{batch.length > 1 ? `Publish ${complete.length} complete` : "Publish"}</button>
        </section>
      </div>
      {shownConfirmation && <PlannerDialog title={shownConfirmation.title} onClose={() => setConfirming(null)}
        actions={<button type="button" className="btn btn-primary" onClick={() => publish(confirming.ids)}>{shownConfirmation.late ? "Publish anyway" : "Publish"}</button>}>
        {shownConfirmation.lines.map(line => <p key={line} className="mb-0">{line}</p>)}
      </PlannerDialog>}
    </div>
  }

  return <div className="ep-stack ep-gap">
    <QueueBar query={query} onCountry={onCountry}>
      <span className="ep-meta">Confirmed proposals, by start date. Publishing creates the event, all its competitions without fees and its logistics
        sheet in one operation.</span>
    </QueueBar>
    {read.error && <Alert color="danger">{read.error}{" "}<button type="button" className="btn btn-link p-0 align-baseline" onClick={read.reload}>Retry</button></Alert>}
    {!read.data && !read.error && <div className="ep-loading"><Spinner size="sm" /> Loading Ready to publish…</div>}
    {read.data && (review ? reviewPage() : list())}
  </div>
}
